// Employee Goals Management - Issue #15
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Target,
    Plus,
    Edit2,
    Trash2,
    CheckCircle,
    Clock,
    AlertCircle,
    Calendar,
    TrendingUp,
    Filter,
    Search,
    ArrowLeft,
    ChevronDown
} from 'lucide-react';
import goalsService from '../../services/goals.service';
import { useAuth } from '../../hooks/useAuth';

const EmployeeGoals = ({ employeeId }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [goals, setGoals] = useState([]);
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        completed: 0,
        cancelled: 0,
        on_hold: 0,
        pending_review: 0,
        averageCompletion: 0
    });
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingGoal, setEditingGoal] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [showStatusDropdown, setShowStatusDropdown] = useState(null);

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        target_date: '',
        priority: 'medium',
        category: '',
        completion_percentage: 0
    });

    useEffect(() => {
        fetchGoals();
    }, [employeeId, statusFilter, priorityFilter]);

    const fetchGoals = async () => {
        try {
            setLoading(true);
            const filters = {};
            if (statusFilter !== 'all') filters.status = statusFilter;
            if (priorityFilter !== 'all') filters.priority = priorityFilter;

            const response = await goalsService.getEmployeeGoals(employeeId, filters);
            setGoals(response.data || []);
            setStats(response.stats || stats);
        } catch (error) {
            console.error('Error fetching goals:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingGoal) {
                await goalsService.updateGoal(editingGoal.id, formData);
            } else {
                await goalsService.createGoal({
                    ...formData,
                    employee_id: employeeId
                });
            }
            setShowModal(false);
            resetForm();
            fetchGoals();
        } catch (error) {
            console.error('Error saving goal:', error);
            alert(error.message || 'Failed to save goal');
        }
    };

    const handleEdit = (goal) => {
        setEditingGoal(goal);
        setFormData({
            title: goal.title,
            description: goal.description || '',
            target_date: goal.target_date ? goal.target_date.split('T')[0] : '',
            priority: goal.priority,
            category: goal.category || '',
            completion_percentage: goal.completion_percentage
        });
        setShowModal(true);
    };

    const handleDelete = async (goalId) => {
        if (!window.confirm('Are you sure you want to delete this goal?')) return;

        try {
            await goalsService.deleteGoal(goalId);
            fetchGoals();
        } catch (error) {
            console.error('Error deleting goal:', error);
            alert('Failed to delete goal');
        }
    };

    const handleComplete = async (goalId) => {
        try {
            await goalsService.completeGoal(goalId);
            fetchGoals();
        } catch (error) {
            console.error('Error completing goal:', error);
            alert('Failed to mark goal as complete');
        }
    };

    const handleProgressUpdate = async (goalId, newProgress) => {
        try {
            await goalsService.updateGoal(goalId, {
                completion_percentage: parseInt(newProgress)
            });
            fetchGoals();
        } catch (error) {
            console.error('Error updating progress:', error);
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            target_date: '',
            priority: 'medium',
            category: '',
            completion_percentage: 0
        });
        setEditingGoal(null);
    };

    const handleGoToTaskBox = () => {
        // Navigate to Task Box feature based on user role
        const userRole = user?.role || 'employee';
        switch (userRole) {
            case 'admin':
                navigate('/admin/taskbox?view=personal');
                break;
            case 'manager':
                navigate('/manager/taskbox?view=personal');
                break;
            case 'employee':
                navigate('/employee/taskbox?view=personal');
                break;
            default:
                navigate('/employee/taskbox?view=personal');
        }
    };

    const handleStatusUpdate = async (goalId, newStatus) => {
        try {
            await goalsService.updateGoal(goalId, { status: newStatus });
            fetchGoals();
        } catch (error) {
            console.error('Error updating goal status:', error);
            alert('Failed to update goal status');
        }
    };

    const handleCompleteGoal = async (goalId) => {
        try {
            await goalsService.completeGoal(goalId);
            fetchGoals();
        } catch (error) {
            console.error('Error completing goal:', error);
            alert('Failed to complete goal');
        }
    };

    const getPriorityColor = (priority) => {
        const colors = {
            critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
            high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
            medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
            low: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
        };
        return colors[priority] || colors.medium;
    };

    const getStatusIcon = (status) => {
        const icons = {
            active: <Clock className="w-4 h-4 text-blue-600" />,
            completed: <CheckCircle className="w-4 h-4 text-green-600" />,
            on_hold: <AlertCircle className="w-4 h-4 text-yellow-600" />,
            pending_review: <Clock className="w-4 h-4 text-orange-600" />,
            cancelled: <AlertCircle className="w-4 h-4 text-red-600" />
        };
        return icons[status] || icons.active;
    };

    const filteredGoals = goals.filter(goal => {
        const matchesSearch = searchQuery === '' ||
            goal.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            goal.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            goal.category?.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesSearch;
    });

    const isOverdue = (targetDate, status) => {
        if (status === 'completed' || status === 'cancelled') return false;
        if (!targetDate) return false;
        return new Date(targetDate) < new Date();
    };

    return (
        <div className="p-6">
            {/* Header with Stats */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                    <Target className="w-8 h-8 text-blue-600 dark:text-blue-400 mr-3" />
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">My Goals</h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            Track and manage your performance goals
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleGoToTaskBox}
                        className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5 mr-2" />
                        Go to Task Box
                    </button>
                    <button
                        onClick={() => {
                            resetForm();
                            setShowModal(true);
                        }}
                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <Plus className="w-5 h-5 mr-2" />
                        Add Goal
                    </button>
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total Goals</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg shadow p-4">
                    <p className="text-sm text-blue-600 dark:text-blue-400">Active</p>
                    <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">{stats.active}</p>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg shadow p-4">
                    <p className="text-sm text-green-600 dark:text-green-400">Completed</p>
                    <p className="text-2xl font-bold text-green-700 dark:text-green-400">{stats.completed}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg shadow p-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Cancelled</p>
                    <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">{stats.cancelled}</p>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg shadow p-4">
                    <p className="text-sm text-purple-600 dark:text-purple-400">Avg. Progress</p>
                    <p className="text-2xl font-bold text-purple-700 dark:text-purple-400">{stats.averageCompletion}%</p>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            <Search className="w-4 h-4 mr-2" />
                            Search
                        </label>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search goals..."
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>
                    <div>
                        <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            <Filter className="w-4 h-4 mr-2" />
                            Status
                        </label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        >
                            <option value="all">All Status</option>
                            <option value="active">Active</option>
                            <option value="completed">Completed</option>
                            <option value="on_hold">On Hold</option>
                            <option value="pending_review">Pending Review</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                    </div>
                    <div>
                        <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            <TrendingUp className="w-4 h-4 mr-2" />
                            Priority
                        </label>
                        <select
                            value={priorityFilter}
                            onChange={(e) => setPriorityFilter(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        >
                            <option value="all">All Priorities</option>
                            <option value="critical">Critical</option>
                            <option value="high">High</option>
                            <option value="medium">Medium</option>
                            <option value="low">Low</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Goals List */}
            {loading ? (
                <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600 dark:text-gray-400">Loading goals...</p>
                </div>
            ) : filteredGoals.length === 0 ? (
                <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow">
                    <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400 mb-2">No goals found</p>
                    <p className="text-sm text-gray-500 dark:text-gray-500">
                        Create your first goal to start tracking your progress
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {filteredGoals.map((goal) => (
                        <div
                            key={goal.id}
                            className={`bg-white dark:bg-gray-800 rounded-lg shadow p-6 ${
                                isOverdue(goal.target_date, goal.status) ? 'border-l-4 border-red-500' : ''
                            }`}
                        >
                            {/* Goal Header */}
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                                        {goal.title}
                                    </h3>
                                    {goal.category && (
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                            {goal.category}
                                        </span>
                                    )}
                                </div>
                                <div className="flex space-x-2">
                                    {goal.status === 'active' && (
                                        <button
                                            onClick={() => handleComplete(goal.id)}
                                            className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                                            title="Mark as complete"
                                        >
                                            <CheckCircle className="w-5 h-5" />
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleEdit(goal)}
                                        className="p-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                                    >
                                        <Edit2 className="w-5 h-5" />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(goal.id)}
                                        className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                                    >
                                        <Trash2 className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Description */}
                            {goal.description && (
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                                    {goal.description}
                                </p>
                            )}

                            {/* Progress Bar */}
                            <div className="mb-3">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Progress
                                    </span>
                                    <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                                        {goal.completion_percentage}%
                                    </span>
                                </div>
                                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                                    <div
                                        className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                                        style={{ width: `${goal.completion_percentage}%` }}
                                    />
                                </div>
                                {goal.status === 'active' && (
                                    <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        value={goal.completion_percentage}
                                        onChange={(e) => handleProgressUpdate(goal.id, e.target.value)}
                                        className="w-full mt-2 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 accent-blue-600"
                                    />
                                )}
                            </div>

                            {/* Footer */}
                            <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
                                <div className="flex items-center space-x-3">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(goal.priority)}`}>
                                        {goal.priority}
                                    </span>
                                    <div className="relative inline-flex items-center">
                                    <button
                                        onClick={() => setShowStatusDropdown(showStatusDropdown === goal.id ? null : goal.id)}
                                        className="inline-flex items-center text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                                    >
                                        {getStatusIcon(goal.status)}
                                        <span className="ml-1 capitalize">{goal.status}</span>
                                        <ChevronDown className="w-3 h-3 ml-1" />
                                    </button>

                                    {showStatusDropdown === goal.id && (
                                        <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 min-w-[150px]">
                                            {goal.status !== 'active' && (
                                                <button
                                                    onClick={() => {
                                                        handleStatusUpdate(goal.id, 'active');
                                                        setShowStatusDropdown(null);
                                                    }}
                                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                                >
                                                    <Clock className="w-4 h-4 text-blue-600" />
                                                    Set Active
                                                </button>
                                            )}
                                            {goal.status !== 'completed' && (
                                                <button
                                                    onClick={() => {
                                                        handleCompleteGoal(goal.id);
                                                        setShowStatusDropdown(null);
                                                    }}
                                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                                >
                                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                                    Mark Complete
                                                </button>
                                            )}
                                            {goal.status !== 'on_hold' && (
                                                <button
                                                    onClick={() => {
                                                        handleStatusUpdate(goal.id, 'on_hold');
                                                        setShowStatusDropdown(null);
                                                    }}
                                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                                >
                                                    <AlertCircle className="w-4 h-4 text-yellow-600" />
                                                    Put On Hold
                                                </button>
                                            )}
                                            {goal.status !== 'pending_review' && (
                                                <button
                                                    onClick={() => {
                                                        handleStatusUpdate(goal.id, 'pending_review');
                                                        setShowStatusDropdown(null);
                                                    }}
                                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                                >
                                                    <Clock className="w-4 h-4 text-orange-600" />
                                                    Pending Review
                                                </button>
                                            )}
                                            {goal.status !== 'cancelled' && (
                                                <button
                                                    onClick={() => {
                                                        handleStatusUpdate(goal.id, 'cancelled');
                                                        setShowStatusDropdown(null);
                                                    }}
                                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-red-600"
                                                >
                                                    <AlertCircle className="w-4 h-4" />
                                                    Cancel
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                                </div>
                                {goal.target_date && (
                                    <div className={`flex items-center text-xs ${
                                        isOverdue(goal.target_date, goal.status)
                                            ? 'text-red-600 dark:text-red-400 font-semibold'
                                            : 'text-gray-600 dark:text-gray-400'
                                    }`}>
                                        <Calendar className="w-3.5 h-3.5 mr-1" />
                                        {new Date(goal.target_date).toLocaleDateString()}
                                        {isOverdue(goal.target_date, goal.status) && ' (Overdue)'}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Add/Edit Goal Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                                {editingGoal ? 'Edit Goal' : 'Create New Goal'}
                            </h3>

                            <form onSubmit={handleSubmit}>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Goal Title *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.title}
                                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                            placeholder="e.g., Complete React Training"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Description
                                        </label>
                                        <textarea
                                            rows="3"
                                            value={formData.description}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                            placeholder="Describe the goal and expected outcomes..."
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                Category
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.category}
                                                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                                placeholder="e.g., Technical Skills"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                Priority *
                                            </label>
                                            <select
                                                required
                                                value={formData.priority}
                                                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                            >
                                                <option value="low">Low</option>
                                                <option value="medium">Medium</option>
                                                <option value="high">High</option>
                                                <option value="critical">Critical</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                Target Date
                                            </label>
                                            <input
                                                type="date"
                                                value={formData.target_date}
                                                onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                            />
                                        </div>

                                        {editingGoal && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                    Progress (%)
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    value={formData.completion_percentage}
                                                    onChange={(e) => setFormData({ ...formData, completion_percentage: parseInt(e.target.value) })}
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="flex justify-end space-x-3 mt-6">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowModal(false);
                                            resetForm();
                                        }}
                                        className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                    >
                                        {editingGoal ? 'Update Goal' : 'Create Goal'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployeeGoals;
